import type { ChatToolActivityStatus, RuntimeSseEvent } from '@/features/shared/chat/types'

const TOOL_EVENT_NAMES = new Set(['tool_end', 'tool_error', 'tool_progress', 'tool_start'])
type ToolEndStatus = Exclude<ChatToolActivityStatus, 'running'>

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function readString(value: unknown) {
  return typeof value === 'string' ? value : undefined
}

function readNonEmptyString(value: unknown) {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

function readNullableString(value: unknown) {
  return typeof value === 'string' || value === null ? value : undefined
}

function readRecord(value: unknown) {
  return isRecord(value) ? value : undefined
}

function readFiniteNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function isToolEndStatus(value: unknown): value is ToolEndStatus {
  return value === 'cancelled' || value === 'error' || value === 'success' || value === 'unknown'
}

function readRuntimeEnvelope(payload: Record<string, unknown>) {
  const envelope: {
    conversation_id?: string | null
    message_id?: string | null
    run_id?: string | null
    sequence?: number
    task_id?: string | null
    terminal?: boolean
    version?: string
  } = {}

  if (typeof payload.version === 'string') {
    envelope.version = payload.version
    const conversationId = readNullableString(payload.conversation_id)
    const messageId = readNullableString(payload.message_id)
    const runId = readNullableString(payload.run_id)
    const taskId = readNullableString(payload.task_id)

    if (conversationId !== undefined) envelope.conversation_id = conversationId
    if (messageId !== undefined) envelope.message_id = messageId
    if (runId !== undefined) envelope.run_id = runId
    if (taskId !== undefined) envelope.task_id = taskId
  }
  if (typeof payload.sequence === 'number' && Number.isInteger(payload.sequence)) {
    envelope.sequence = payload.sequence
  }
  if (typeof payload.terminal === 'boolean') {
    envelope.terminal = payload.terminal
  }

  return envelope
}

function normalizeToolEvent(
  payload: Record<string, unknown>,
  event: string,
  envelope: ReturnType<typeof readRuntimeEnvelope>,
): RuntimeSseEvent {
  const runId = readNonEmptyString(payload.run_id)
  const invocationId = readNonEmptyString(payload.invocation_id)
  const turnId = readNonEmptyString(payload.turn_id)
  const toolCallId = readNonEmptyString(payload.tool_call_id)

  // Tool UI 至少需要 run + invocation；旧 producer 只能使用完整的 run + turn + call fallback。
  // 字段不完整时仍保留 v2 cursor，但 raw payload 不进入前端 Tool contract。
  if (!runId || (!invocationId && !(turnId && toolCallId))) {
    return { ...envelope, event: 'unknown', raw: { event } }
  }

  const common = {
    ...envelope,
    conversation_id: readNullableString(payload.conversation_id),
    duration_ms: readFiniteNumber(payload.duration_ms),
    error_code: readString(payload.error_code),
    invocation_id: invocationId,
    message_id: readNullableString(payload.message_id ?? payload.id),
    run_id: runId,
    task_id: readNullableString(payload.task_id),
    tool_call_id: toolCallId,
    tool_display_name: readString(payload.tool_display_name),
    tool_name: readString(payload.tool_name),
    turn_id: turnId,
  }

  if (event === 'tool_start') {
    return { ...common, event, status: 'running' }
  }
  if (event === 'tool_progress') {
    return {
      ...common,
      event,
      progress: readFiniteNumber(payload.progress),
      status: 'running',
      total: readFiniteNumber(payload.total),
    }
  }
  if (event === 'tool_error') {
    return { ...common, event, status: 'error' }
  }
  if (event === 'tool_end' && isToolEndStatus(payload.status)) {
    return { ...common, event, status: payload.status }
  }

  return { ...envelope, event: 'unknown', raw: { event } }
}

/**
 * 把不同 provider 或历史接口返回的 SSE payload 收敛为前端稳定事件。
 * 未识别字段保留为 `unknown`，避免新事件上线时直接丢数据。
 */
export function normalizeRuntimeSsePayload(payload: unknown): RuntimeSseEvent {
  if (!isRecord(payload)) {
    return { event: 'unknown', raw: { value: payload } }
  }

  const envelope = readRuntimeEnvelope(payload)
  const event = readString(payload.event)

  // provider 在 SSE 建立之后可能用 native error 帧终止运行（Dify 的 `event: error`，
  // 错误文案在 message 上）。它同样是流级失败，必须归一为 stream_error，否则消费方
  // 只会看到一个未识别事件，并把失败的运行当作正常结束。
  if (
    payload.code === 'stream_error' ||
    event === 'stream_error' ||
    event === 'error' ||
    (!event && typeof payload.error === 'string')
  ) {
    const data = readRecord(payload.data)
    const diagnostics = readRecord(payload.diagnostics) ?? readRecord(data?.diagnostics)
    const errorLabel = readString(payload.error_label)
    const errorSource = readString(payload.error_source)

    return {
      ...envelope,
      code: readString(payload.code),
      ...(diagnostics ? { diagnostics } : {}),
      error: readString(payload.error ?? payload.message),
      error_code: readString(payload.error_code),
      ...(errorLabel ? { error_label: errorLabel } : {}),
      ...(errorSource ? { error_source: errorSource } : {}),
      event: 'stream_error',
    }
  }

  if (event && TOOL_EVENT_NAMES.has(event)) {
    return normalizeToolEvent(payload, event, envelope)
  }

  if (event === 'message_delta' || event === 'thought_delta') {
    // 不同 runtime 可能使用 delta、answer 或 text 表示增量内容。
    return {
      ...envelope,
      conversation_id: readNullableString(payload.conversation_id),
      delta: readString(payload.delta ?? payload.answer ?? payload.text) ?? '',
      event,
      message_id: readNullableString(payload.message_id ?? payload.id),
      run_id: readNullableString(payload.run_id),
      task_id: readNullableString(payload.task_id),
      text: readString(payload.text ?? payload.answer),
    }
  }

  if (event === 'message_end') {
    return {
      ...envelope,
      conversation_id: readNullableString(payload.conversation_id),
      event,
      files: Array.isArray(payload.files) ? payload.files : undefined,
      id: readNullableString(payload.id),
      message_id: readNullableString(payload.message_id ?? payload.id),
      metadata: isRecord(payload.metadata) ? payload.metadata : undefined,
      run_id: readNullableString(payload.run_id),
      task_id: readNullableString(payload.task_id),
      usage: isRecord(payload.usage) ? payload.usage : undefined,
    }
  }

  if (event === 'message_replace') {
    return {
      ...envelope,
      answer: readString(payload.answer) ?? '',
      conversation_id: readNullableString(payload.conversation_id),
      event,
      message_id: readNullableString(payload.message_id ?? payload.id),
      run_id: readNullableString(payload.run_id),
      task_id: readNullableString(payload.task_id),
    }
  }

  if (event === 'message_file' || event === 'trace_event') {
    return { ...payload, event }
  }

  if (event === 'runtime_meta') {
    return {
      ...envelope,
      conversation_id: readNullableString(payload.conversation_id),
      event,
      message_id: readNullableString(payload.message_id ?? payload.id),
      run_id: readNullableString(payload.run_id),
      task_id: readNullableString(payload.task_id),
    }
  }

  if (event === 'stop_confirmed') {
    return {
      ...envelope,
      conversation_id: readNullableString(payload.conversation_id),
      event,
      message_id: readNullableString(payload.message_id ?? payload.record_id ?? payload.id),
      record_id: readNullableString(payload.record_id ?? payload.message_id ?? payload.id),
      run_id: readNullableString(payload.run_id),
      task_id: readNullableString(payload.task_id),
    }
  }

  if (event === 'stream_cancelled') {
    return {
      ...envelope,
      conversation_id: readNullableString(payload.conversation_id),
      event,
      message_id: readNullableString(payload.message_id ?? payload.id),
      reason: readString(payload.reason),
      run_id: readNullableString(payload.run_id),
      task_id: readNullableString(payload.task_id),
    }
  }

  return { ...envelope, event: 'unknown', raw: payload }
}

/**
 * 解析一段可能不完整的 SSE 文本。
 * 返回 `rest` 交给下一次 chunk 拼接，避免半个 JSON 帧被提前解析。
 */
export function parseSseChunk(buffer: string) {
  const events: RuntimeSseEvent[] = []
  const parts = buffer.split(/\r?\n\r?\n/)
  const rest = parts.pop() ?? ''

  for (const part of parts) {
    const dataLines = part
      .split(/\r?\n/)
      .map((line) => line.trimEnd())
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())

    if (dataLines.length === 0) {
      continue
    }

    const data = dataLines.join('\n').trim()

    if (!data || data === '[DONE]') {
      continue
    }

    try {
      events.push(normalizeRuntimeSsePayload(JSON.parse(data) as unknown))
    } catch {
      // JSON 解析失败说明流已无法可信恢复，向上抛出统一的 stream_error。
      events.push({
        error: 'Invalid stream payload',
        error_code: 'app_runtime.stream_error',
        event: 'stream_error',
      })
    }
  }

  return { events, rest }
}
